import { Injectable, type Signal, signal } from '@angular/core';

/**
 * The one link between the user menu and Clarity's organization-panel access read (EFF-26575, spec → TR-8).
 *
 * The menu sits in the platform shell, above every section, and cannot see a single Clarity provider —
 * the transports, the Clarity session and its query client are bound on the lazily loaded Clarity routes
 * and must stay there, or the Clarity chunk moves into the initial one. So this root service holds only a
 * boolean, and knows nothing about how it is decided: no HTTP, no Clarity import. The Clarity composition
 * root fills it from `GET /admin-panel/access` through `provideClarityOrganizationMenuLink()`, which
 * answers `true` only while a Clarity session exists and that read says the caller holds a grant.
 *
 * It therefore starts `false` and stays `false` until the reader has entered the Clarity section once in
 * this session (spec → TR-9) — the same limit the *Admin Panel* entry's `ClarityAdminRoleHint` has. It is
 * root-scoped and the platform logout is an in-app navigation, so the menu resets it on log out rather
 * than letting it outlive the user who earned it.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationMenuBridgeService {
	private readonly current = signal(false);

	/** Whether the menu may offer the *Organization* entry; `false` while it must not. */
	public readonly hasAccess: Signal<boolean> = this.current.asReadonly();

	public set(hasAccess: boolean): void {
		this.current.set(hasAccess);
	}
}
